<template>
	<div>
		<section class="flex max-sm:flex-col max-sm:items-start flex-wrap gap-2 sm:gap-4 items-center justify-between mb-4">
			<div>
				<span class="text-h3 font-bold text-neutral-700 font-secondary" aria-label="Hypermarket">
					{{ blok.title }}
				</span>
				<h1 class="typo-h1" v-text="hypermarket.content.name" />
			</div>
			<button
				class="btn btn-xs btn-secondary shadow-green-sm"
				type="button"
				aria-label="Změnit pobočku"
				@click="canRenderHypermarketSelector = true"
			>
				<NuxtIcon
					name="chevron-right"
					class="text-lg transition duration-300"
					filled
					aria-hidden="true"
				/>
				Změnit pobočku
			</button>
		</section>
		<section :class="INFO_BLOCK_CLASS" aria-labelledby="opening-hours-heading">
			<h2 id="opening-hours-heading" class="block mb-4 text-h3 font-bold font-secondary">
				Otevírací doba
			</h2>
			<div class="flex gap-2">
				<NuxtIcon
					name="clock"
					class="text-base text-icon-primary pt-0.5 sm:pt-1 max-sm:hidden"
					filled
					aria-hidden="true"
				/>
				<OpeningHours
					:hours="blok.openingHours"
					:holidays="blok.holidayOpeningHours"
					:is-closed="blok.state === 'close' ? true : false"
					use-full-day-name
					class="flex flex-col grow gap-x-4 gap-y-2 [&_ul]:block [&_ul]:space-y-2 [&_ul]:ml-auto [&_ul>li]:!mb-0 [&_strong]:min-w-[3.125rem] [&_span]:ml-auto [&_span]:mb-0 [&>div]:mb-4 [&>div]:last:mb-0"
				/>
			</div>
		</section>
		<section v-if="blok.specificRestaurantServices?.length" :class="INFO_BLOCK_CLASS" aria-labelledby="services-heading">
			<h2 id="services-heading" class="sr-only">
				Služby
			</h2>
			<div class="flex gap-2 sm:pl-6">
				<RestaurantServices
					:services="blok.specificRestaurantServices"
				/>
			</div>
		</section>
		<section v-if="blok.phone || blok.email" :class="INFO_BLOCK_CLASS" aria-labelledby="contact-info-heading">
			<h2 id="contact-info-heading" class="sr-only">
				Kontaktní informace
			</h2>
			<div class="max-sm:grid max-sm:grid-cols-1 sm:flex sm:flex-wrap sm:justify-between gap-2 sm:items-center">
				<span class="flex items-center gap-2">
					<NuxtIcon
						name="phone"
						class="text-base text-icon-primary sm:pt-[.125rem] relative -scale-x-100"
						filled
						aria-hidden="true"
					/>
					<span class="mr-auto text-sm md:text-base">Bezplatná infolinka</span>
				</span>
				<div class="max-sm:grid max-sm:grid-cols-2 sm:flex gap-4 sm:justify-between text-sm md:text-base">
					<a
						:href="getPhoneLink(blok.phone)"
						class="font-bold underline text-body-link hover:decoration-transparent"
						:aria-label="`Zavolat na bezplatnou infolinku ${blok.phone}`"
						v-text="blok.phone"
					/>
					<a
						:href="`mailto:${blok.email}`"
						class="underline text-body-link hover:decoration-transparent"
						:aria-label="`Napsat e-mail na adresu ${blok.email}`"
						v-text="blok.email"
					/>
				</div>
			</div>
		</section>
		<section v-if="blok.street || blok.city || blok.zipCode || blok.mapUrl?.length" :class="[INFO_BLOCK_CLASS, 'flex flex-col gap-4']" aria-labelledby="address-heading">
			<h2 id="address-heading" class="sr-only">
				Další informace o pobočce
			</h2>
			<address class="flex flex-wrap gap-4 items-center justify-between">
				<h3 class="sr-only">
					Adresa pobočky
				</h3>
				<div class="flex gap-2 not-italic">
					<NuxtIcon
						name="map-pin"
						class="text-base text-icon-primary sm:pt-[.1875rem]"
						filled
						aria-hidden="true"
					/>
					<span class="flex flex-col text-sm md:text-base">
						<span v-text="blok.street" />
						<span v-text="blok.city" />
						<span v-if="blok.zipCode" v-text="blok.zipCode" />
					</span>
				</div>
				<StoryblokComponent
					v-for="item in blok.mapUrl"
					:key="item.id"
					:blok="item"
					class="btn btn-xs btn-secondary not-italic"
					has-icon-name="map"
					has-icon-class="text-base"
					has-icon-left
				/>
			</address>
			<div v-if="blok.parking">
				<h3 class="sr-only">
					Parkování
				</h3>
				<div class="flex gap-2">
					<NuxtIcon
						name="parking"
						class="text-lg text-icon-primary sm:pt-0.5"
						filled
						aria-hidden="true"
					/>
					<LazyRichTextEditor v-if="blok.parking" :blok="blok.parking" class="text-sm md:text-base" />
				</div>
			</div>
			<div v-if="blok.publicTransport">
				<h3 class="sr-only">
					MHD
				</h3>
				<div class="flex gap-2">
					<NuxtIcon
						name="bus"
						class="text-xl text-icon-primary sm:pt-0.5"
						filled
						aria-hidden="true"
					/>
					<LazyRichTextEditor v-if="blok.publicTransport" :blok="blok.publicTransport" class="text-sm md:text-base" />
				</div>
			</div>
		</section>
		<PartialsHypermarketSelectionPopup
			v-model:visible="canRenderHypermarketSelector"
			change-with-redirect
		/>
	</div>
</template>

<script setup lang="ts">
	import {storeToRefs} from 'pinia';
	import {useStoryblokHypermarketsStore} from '~/stores/storyblokHypermarkets';
	import {getPhoneLink} from '@/utils';
	import type {PageTypeRestaurantStoryblok} from '@/storyblok/types';

	const INFO_BLOCK_CLASS = 'py-4 border-t border-border-secondary';

	type Props = {
		branch: string;
		blok: PageTypeRestaurantStoryblok;
	};

	const props = defineProps<Props>();

	const canRenderHypermarketSelector = ref<boolean>(false);

	const hypermarketsStore = useStoryblokHypermarketsStore();
	const {hypermarketsWithGlobusBySlug} = storeToRefs(hypermarketsStore);

	const hypermarket = computed(() => hypermarketsWithGlobusBySlug.value[props.branch]);
</script>
